import React from 'react';

import {
  Box,
  Paper,
  Stack,
  Typography,
  TextField,
  MenuItem,
  Button,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Drawer,
} from '@mui/material';

import RefreshIcon from '@mui/icons-material/Refresh';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import SummarizeIcon from '@mui/icons-material/Summarize';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import TuneIcon from '@mui/icons-material/Tune';
import DownloadIcon from '@mui/icons-material/Download';
import SourceIcon from '@mui/icons-material/Source';
import BugReportIcon from '@mui/icons-material/BugReport';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import Client from './components/Client';
import Config from './components/ClientConfig';


const LEVEL_LABELS = {
  1: 'Info',
  2: 'Warning',
  3: 'Error',
  4: 'Critical',
};

const SOURCE_OPTIONS = [
  'orders',
  'payments',
  'promotions',
  'inventory',
  'mobile_app',
  'pos',
  'integrations',
];

const EVENT_TYPE_OPTIONS = [
  'order_add_failed',
  'payment_callback_failed',
  'promotion_validation_failed',
  'integration_failed',
];

const SUMMARY_ROW_HEIGHT = 68;
const LOG_ROW_HEIGHT = 68;
const compactSummaryCellSx = {
  height: SUMMARY_ROW_HEIGHT,
  maxHeight: SUMMARY_ROW_HEIGHT,
  py: 0.75,
  verticalAlign: 'top',
  overflow: 'hidden',
};
const compactLogCellSx = {
  height: LOG_ROW_HEIGHT,
  maxHeight: LOG_ROW_HEIGHT,
  py: 0.75,
  verticalAlign: 'top',
  overflow: 'hidden',
};

function ClampedText({ children, title, lines = 1, variant = 'body2', color, sx = {} }) {
  return (
    <Typography
      variant={variant}
      color={color}
      title={title}
      sx={{
        display: '-webkit-box',
        WebkitBoxOrient: 'vertical',
        WebkitLineClamp: lines,
        overflow: 'hidden',
        overflowWrap: 'anywhere',
        lineHeight: 1.35,
        ...sx,
      }}
    >
      {children}
    </Typography>
  );
}


function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

function dateDaysAgoISO(days) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

function formatDateTime(value) {
  if (!value) return '-';

  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;

    return d.toLocaleString('ro-RO', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch (e) {
    return value;
  }
}

function getOccurrenceCount(row) {
  const value = Number(row.occurrenceCount || row.occurrence_count || 1);
  if (Number.isNaN(value) || value < 1) {
    return 1;
  }
  return value;
}

function getLastSeenOn(row) {
  return row.lastSeenOn || row.last_seen_on || row.createdOn;
}

function formatLevelLabel(value) {
  return LEVEL_LABELS[Number(value)] || `Level ${value}`;
}

function getLevelColor(level) {
  const value = Number(level);

  if (value === 4) return 'error';
  if (value === 3) return 'error';
  if (value === 2) return 'warning';
  if (value === 1) return 'info';

  return 'default';
}

function getLevelVariant(level) {
  const value = Number(level);

  if (value >= 3) return 'filled';

  return 'outlined';
}

function normalizeText(value) {
  if (value === null || value === undefined || value === '') return '-';

  return String(value);
}

function getLocationName(row) {
  return row.location_name || row.location_iref || '';
}

function getUserLabel(row) {
  const username = String(row.username || '').trim();
  const firstName = String(row.user_first_name || '').trim();
  const userId = row.user_id === null || row.user_id === undefined
    ? ''
    : String(row.user_id).trim();
  const userNameOrId = firstName || userId;

  return [username, userNameOrId].filter(Boolean).join(' / ') || '-';
}

function getClientPlatformLabel(row) {
  const payload = row && row.payload;
  const context = payload && typeof payload === 'object'
    ? payload.client_context
    : null;
  const platform = context && typeof context === 'object'
    ? String(context.platform || '').trim().toLowerCase()
    : '';

  if (platform === 'ios') return 'iOS';
  if (platform === 'android') return 'Android';

  return '';
}

function getEventGroupKey(row) {
  return [
    row.source || 'unknown',
    row.event_type || 'unknown',
    row.error_code || 'no_code',
  ].join('::');
}

function getEventGroupName(row) {
  const source = row.source || 'unknown';
  const eventType = row.event_type || 'unknown';
  const errorCode = row.error_code || 'no code';

  return `${source} / ${eventType} / ${errorCode}`;
}

function getEventGroupSubtitle(row) {
  const parts = [];

  if (row.source) parts.push(`Source: ${row.source}`);
  if (row.event_type) parts.push(`Event: ${row.event_type}`);
  if (row.error_code) parts.push(`Code: ${row.error_code}`);

  return parts.join(' · ') || '-';
}

function buildEventSummary(logs) {
  const map = {};

  logs.forEach((row) => {
    const key = getEventGroupKey(row);

    if (!map[key]) {
      map[key] = {
        key,
        name: getEventGroupName(row),
        subtitle: getEventGroupSubtitle(row),
        source: row.source || 'unknown',
        event_type: row.event_type || 'unknown',
        error_code: row.error_code || '',
        error_code_description: row.error_code_description || '',
        total: 0,
        rowCount: 0,
        criticalCount: 0,
        errorCount: 0,
        warningCount: 0,
        infoCount: 0,
        attentionScore: 0,
        lastEvent: null,
        lastEventTs: 0,
        sampleMessage: row.message || '',
      };
    }

    const occurrenceCount = getOccurrenceCount(row);
    map[key].total += occurrenceCount;
    map[key].rowCount += 1;

    const level = Number(row.level);

    if (level === 4) {
      map[key].criticalCount += occurrenceCount;
    } else if (level === 3) {
      map[key].errorCount += occurrenceCount;
    } else if (level === 2) {
      map[key].warningCount += occurrenceCount;
    } else if (level === 1) {
      map[key].infoCount += occurrenceCount;
    }

    const lastSeenOn = getLastSeenOn(row);
    const lastSeenTs = lastSeenOn
      ? new Date(lastSeenOn).getTime()
      : 0;

    if (!Number.isNaN(lastSeenTs) && lastSeenTs > (map[key].lastEventTs || 0)) {
      map[key].lastEvent = lastSeenOn;
      map[key].lastEventTs = lastSeenTs;
      map[key].sampleMessage = row.message || map[key].sampleMessage;
    }
  });

  return Object.values(map)
    .map((group) => ({
      ...group,
      attentionScore:
        (group.criticalCount * 3) +
        (group.errorCount * 2) +
        group.warningCount,
    }))
    .sort((a, b) => {
      // 1. Attention level first
      if (b.attentionScore !== a.attentionScore) {
        return b.attentionScore - a.attentionScore;
      }
      // 2. Most recent event first
      if ((b.lastEventTs || 0) !== (a.lastEventTs || 0)) {
        return (b.lastEventTs || 0) - (a.lastEventTs || 0);
      }
      // 3. Source alphabetically
      const sourceCompare = String(a.source || '').localeCompare(
        String(b.source || ''),
        'ro',
        { sensitivity: 'base' }
      );
      if (sourceCompare !== 0) {
        return sourceCompare;
      }
      // 4. Event type alphabetically
      const eventTypeCompare = String(a.event_type || '').localeCompare(
        String(b.event_type || ''),
        'ro',
        { sensitivity: 'base' }
      );
      if (eventTypeCompare !== 0) {
        return eventTypeCompare;
      }
      // 5. Error code alphabetically
      return String(a.error_code || '').localeCompare(
        String(b.error_code || ''),
        'ro',
        { sensitivity: 'base' }
      );
    });
}

function csvEscape(value) {
  if (value === null || value === undefined) return '';

  const text = String(value);

  if (
    text.includes('"') ||
    text.includes(',') ||
    text.includes('\n') ||
    text.includes('\r')
  ) {
    return `"${text.replace(/"/g, '""')}"`;
  }

  return text;
}

function downloadCsv(filename, rows) {
  const csvContent = rows.map((row) => row.map(csvEscape).join(',')).join('\n');
  const blob = new Blob([csvContent], {
    type: 'text/csv;charset=utf-8;',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();

  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function safeFilename(value) {
  return String(value || 'platform_logs')
    .trim()
    .replace(/[^\w\-]+/g, '_')
    .replace(/_+/g, '_')
    .slice(0, 80);
}

function prettyPayload(value) {
  if (!value) return 'No payload available.';

  try {
    if (typeof value === 'string') {
      try {
        return JSON.stringify(JSON.parse(value), null, 2);
      } catch (e) {
        return value;
      }
    }

    return JSON.stringify(value, null, 2);
  } catch (e) {
    return String(value);
  }
}


class PlatformLogs extends React.Component {
  constructor(props) {
    super(props);

    this.client = new Client();

    // Recommended Config value:
    // Config.API_URL_PLATFORM_LOGS = Config.API_URL + 'platformLogs/'
    //
    // If you don't have it yet, replace this line with:
    // this.client.prefix = Config.API_URL + 'platformLogs/';
    this.client.prefix = Config.API_URL_LOGS_PLATFORM || `${Config.API_URL}platformLogs/`;

    this.state = {
      loading: false,
      error: null,

      dateFrom: dateDaysAgoISO(1),
      dateTo: todayISO(),
      level: '',
      source: '',
      eventType: '',
      errorCode: '',
      limit: 500,

      summary: {
        total: 0,
        by_level: [],
        by_source: [],
        by_event_type: [],
        by_error_code: [],
      },
      logs: [],

      selectedGroupKey: null,
      selectedGroupName: '',
      detailsDrawerOpen: false,
      errorsOnly: true,
      filtersOpen: false,
      expanded: {},
    };
  }

  componentDidMount() {
    this.loadReport();
  }

  buildQueryData = () => {
    const {
      dateFrom,
      dateTo,
      level,
      source,
      eventType,
      errorCode,
      limit,
    } = this.state;

    const queryData = {
      date_from: dateFrom,
      date_to: dateTo,
      limit,
    };

    if (level !== '') {
      queryData.level = level;
    }

    if (source && source.trim() !== '') {
      queryData.source = source.trim();
    }

    if (eventType && eventType.trim() !== '') {
      queryData.event_type = eventType.trim();
    }

    if (errorCode && errorCode.trim() !== '') {
      queryData.error_code = errorCode.trim();
    }

    return queryData;
  };

  loadReport = () => {
    this.setState({
      loading: true,
      error: null,
    });

    try {
      const result = this.client.getData('report/', this.buildQueryData());

      if (!result) {
        this.setState({
          loading: false,
          error: 'No response received from platform logs endpoint.',
        });
        return;
      }

      this.setState({
        loading: false,
        summary: result.summary || {
          total: 0,
          by_level: [],
          by_source: [],
          by_event_type: [],
          by_error_code: [],
        },
        logs: result.logs || [],
        selectedGroupKey: null,
        selectedGroupName: '',
        detailsDrawerOpen: false,
        expanded: {},
      });
    } catch (e) {
      this.setState({
        loading: false,
        error: 'Could not load platform logs.',
      });
    }
  };

  resetFilters = () => {
    this.setState(
      {
        dateFrom: dateDaysAgoISO(1),
        dateTo: todayISO(),
        level: '',
        source: '',
        eventType: '',
        errorCode: '',
        limit: 500,
        selectedGroupKey: null,
        selectedGroupName: '',
        detailsDrawerOpen: false,
        expanded: {},
      },
      this.loadReport
    );
  };

  toggleExpanded = (id) => {
    this.setState((prevState) => ({
      expanded: {
        ...prevState.expanded,
        [id]: !prevState.expanded[id],
      },
    }));
  };

  selectGroup = (group) => {
    this.setState({
      selectedGroupKey: group.key,
      selectedGroupName: group.name,
      detailsDrawerOpen: true,
      expanded: {},
    });
  };

  closeDetailsDrawer = () => {
    this.setState({
      detailsDrawerOpen: false,
      expanded: {},
    });
  };

  getVisibleLogs = () => {
    const { logs, errorsOnly } = this.state;

    if (!errorsOnly) {
      return logs;
    }

    return logs.filter((row) => Number(row.level) >= 3);
  };

  renderSummaryCard(title, value, subtitle, icon) {
    return (
      <Paper
        variant="outlined"
        sx={{
          p: { xs: 0.75, sm: 1 },
          borderRadius: 2,
          height: '100%',
          bgcolor: '#fff',
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Box
            sx={{
              width: { xs: 30, sm: 32 },
              height: { xs: 30, sm: 32 },
              borderRadius: 2,
              bgcolor: 'rgba(15, 23, 42, 0.06)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            {icon}
          </Box>

          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary">
              {title}
            </Typography>

            <Typography
              variant="h6"
              sx={{
                lineHeight: 1.15,
                fontWeight: 700,
                fontSize: { xs: 16, sm: 20 },
              }}
            >
              {value}
            </Typography>

            {subtitle ? (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: 'block',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: 260,
                }}
              >
                {subtitle}
              </Typography>
            ) : null}
          </Box>
        </Stack>
      </Paper>
    );
  }

  renderFilterFields() {
    const {
      dateFrom,
      dateTo,
      level,
      source,
      eventType,
      errorCode,
      limit,
      loading,
    } = this.state;

    return (
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'repeat(2, minmax(0, 1fr))',
            sm: 'repeat(3, minmax(0, 1fr))',
            md: '1fr 1fr 0.9fr 1.1fr 1.2fr 1fr 0.8fr auto auto',
          },
          gap: 1,
          alignItems: 'center',
        }}
      >
        <TextField
          size="small"
          type="date"
          label="From"
          value={dateFrom}
          onChange={(e) => this.setState({ dateFrom: e.target.value })}
          InputLabelProps={{ shrink: true }}
        />

        <TextField
          size="small"
          type="date"
          label="To"
          value={dateTo}
          onChange={(e) => this.setState({ dateTo: e.target.value })}
          InputLabelProps={{ shrink: true }}
        />

        <TextField
          select
          size="small"
          label="Level"
          value={level}
          onChange={(e) => this.setState({ level: e.target.value })}
        >
          <MenuItem value="">All levels</MenuItem>
          <MenuItem value={1}>Info</MenuItem>
          <MenuItem value={2}>Warning</MenuItem>
          <MenuItem value={3}>Error</MenuItem>
          <MenuItem value={4}>Critical</MenuItem>
        </TextField>

        <TextField
          select
          size="small"
          label="Source"
          value={source}
          onChange={(e) => this.setState({ source: e.target.value })}
          sx={{
            gridColumn: { xs: 'span 2', sm: 'auto' },
          }}
        >
          <MenuItem value="">All sources</MenuItem>
          {SOURCE_OPTIONS.map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          size="small"
          label="Event type"
          value={eventType}
          onChange={(e) => this.setState({ eventType: e.target.value })}
          sx={{
            gridColumn: { xs: 'span 2', sm: 'auto' },
          }}
        >
          <MenuItem value="">All events</MenuItem>
          {EVENT_TYPE_OPTIONS.map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          size="small"
          label="Error code"
          placeholder="ORDR-020"
          value={errorCode}
          onChange={(e) => this.setState({ errorCode: e.target.value })}
        />

        <TextField
          size="small"
          type="number"
          label="Limit"
          value={limit}
          onChange={(e) => this.setState({ limit: e.target.value })}
          InputProps={{
            inputProps: {
              min: 1,
              max: 1000,
            },
          }}
        />

        <Button
          variant="contained"
          size="small"
          startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />}
          onClick={this.loadReport}
          disabled={loading}
          sx={{
            minHeight: 40,
            gridColumn: { xs: 'span 1', sm: 'auto' },
          }}
        >
          Load
        </Button>

        <Tooltip title="Reset filters">
          <IconButton
            size="small"
            onClick={this.resetFilters}
            sx={{
              border: '1px solid rgba(15, 23, 42, 0.12)',
              borderRadius: 1.5,
              height: 40,
              width: 40,
              justifySelf: { xs: 'end', sm: 'auto' },
            }}
          >
            <FilterAltOffIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    );
  }

  renderFilters() {
    const {
      dateFrom,
      dateTo,
      level,
      source,
      eventType,
      errorCode,
    } = this.state;

    return (
      <Paper
        variant="outlined"
        sx={{
          p: { xs: 0.75, sm: 1.25 },
          borderRadius: 2,
          bgcolor: '#fff',
          flexShrink: 0,
        }}
      >
        <Stack spacing={1}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={1}
          >
            <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
              <TuneIcon fontSize="small" />

              <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Filters
                </Typography>

                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    display: { xs: 'none', sm: 'block' },
                  }}
                >
                  {dateFrom} → {dateTo}
                  {level !== '' ? ` · ${formatLevelLabel(level)}` : ''}
                  {source ? ` · ${source}` : ''}
                  {eventType ? ` · ${eventType}` : ''}
                  {errorCode ? ` · ${errorCode}` : ''}
                </Typography>
              </Box>
            </Stack>

            <Stack direction="row" spacing={0.75} alignItems="center">
              <Button
                size="small"
                variant={this.state.errorsOnly ? 'contained' : 'outlined'}
                color="error"
                startIcon={<WarningAmberIcon fontSize="small" />}
                onClick={() => this.setState({ errorsOnly: !this.state.errorsOnly })}
                sx={{
                  minHeight: 34,
                  whiteSpace: 'nowrap',
                }}
              >
                Errors
              </Button>

              <Button
                size="small"
                variant="outlined"
                onClick={() => this.setState({ filtersOpen: !this.state.filtersOpen })}
                sx={{
                  minHeight: 34,
                  display: { xs: 'inline-flex', sm: 'none' },
                }}
              >
                {this.state.filtersOpen ? 'Hide' : 'Show'}
              </Button>
            </Stack>
          </Stack>

          <Collapse
            in={this.state.filtersOpen}
            timeout="auto"
            unmountOnExit={false}
            sx={{
              display: { xs: 'block', sm: 'none' },
            }}
          >
            {this.renderFilterFields()}
          </Collapse>

          <Box
            sx={{
              display: { xs: 'none', sm: 'block' },
            }}
          >
            {this.renderFilterFields()}
          </Box>
        </Stack>
      </Paper>
    );
  }

  exportSelectedGroupLogs = () => {
    const {
      selectedGroupKey,
      selectedGroupName,
      dateFrom,
      dateTo,
      logs,
    } = this.state;

    if (!selectedGroupKey) {
      return;
    }

    const visibleLogs = logs.filter(
      (row) => getEventGroupKey(row) === selectedGroupKey
    );

    const rows = [
      [
        'First seen',
        'Last seen',
        'Occurrences',
        'Level',
        'Source',
        'Event type',
        'Error code',
        'Error code description',
        'Message',
        'Order code',
        'Client',
        'POS',
        'Location',
        'Object type',
        'Object iref',
        'User ID',
        'Username',
        'User type',
        'Request method',
        'Request path',
        'Payload',
      ],
      ...visibleLogs.map((row) => [
        formatDateTime(row.createdOn),
        formatDateTime(getLastSeenOn(row)),
        getOccurrenceCount(row),
        row.level_label || formatLevelLabel(row.level),
        row.source || '',
        row.event_type || '',
        row.error_code || '',
        row.error_code_description || '',
        row.message || '',
        row.order_code || '',
        row.client_iref || '',
        row.pos_iref || '',
        getLocationName(row),
        row.object_type || '',
        row.object_iref || '',
        row.user_id || '',
        row.username || '',
        row.user_type || '',
        row.request_method || '',
        row.request_path || '',
        prettyPayload(row.payload),
      ]),
    ];

    const filename = [
      'platform_logs',
      safeFilename(selectedGroupName || selectedGroupKey),
      dateFrom || 'from',
      dateTo || 'to',
    ].join('_');

    downloadCsv(`${filename}.csv`, rows);
  };

  renderSummary() {
    const { summary, logs } = this.state;

    const visibleLogs = this.getVisibleLogs();
    const groups = buildEventSummary(visibleLogs);

    const byLevel = summary.by_level || [];
    const bySource = summary.by_source || [];
    const byErrorCode = summary.by_error_code || [];

    const errorsCount = byLevel.reduce((total, row) => {
      if (Number(row.level) >= 3) {
        return total + (row.total || 0);
      }

      return total;
    }, 0);

    const criticalCount = byLevel.reduce((total, row) => {
      if (Number(row.level) === 4) {
        return total + (row.total || 0);
      }

      return total;
    }, 0);

    const topSource = bySource.length ? bySource[0] : null;
    const topErrorCode = byErrorCode.find((row) => row.error_code) || null;

    return (
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'repeat(2, minmax(0, 1fr))',
            md: 'repeat(4, minmax(0, 1fr))',
          },
          gap: 1,
        }}
      >
        {this.renderSummaryCard(
          'Occurrences',
          summary.total || logs.reduce((total, row) => total + getOccurrenceCount(row), 0),
          `${visibleLogs.length} grouped rows visible`,
          <ReceiptLongIcon fontSize="small" />
        )}

        {this.renderSummaryCard(
          'Errors',
          errorsCount,
          criticalCount ? `${criticalCount} critical` : 'No critical logs',
          <ErrorOutlineIcon fontSize="small" />
        )}

        {this.renderSummaryCard(
          'Top source',
          topSource ? topSource.total : 0,
          topSource ? topSource.source : 'No source activity',
          <SourceIcon fontSize="small" />
        )}

        {this.renderSummaryCard(
          'Top error code',
          topErrorCode ? topErrorCode.total : 0,
          topErrorCode ? topErrorCode.error_code : 'No error code',
          <BugReportIcon fontSize="small" />
        )}
      </Box>
    );
  }

  renderLevelChips() {
    const { summary } = this.state;
    const byLevel = summary.by_level || [];

    if (!byLevel.length) {
      return null;
    }

    return (
      <Paper
        variant="outlined"
        sx={{
          px: 1.25,
          py: 0.75,
          borderRadius: 2,
          bgcolor: '#fff',
          display: { xs: 'none', md: 'block' },
          flexShrink: 0,
        }}
      >
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Typography variant="subtitle2" sx={{ fontWeight: 700, flexShrink: 0 }}>
            Activity by level
          </Typography>

          <Stack
            direction="row"
            spacing={0.75}
            flexWrap="wrap"
            sx={{
              rowGap: 0.75,
              minWidth: 0,
            }}
          >
            {byLevel.map((row) => (
              <Chip
                key={row.level}
                size="small"
                label={`${formatLevelLabel(row.level)}: ${row.total}`}
                color={getLevelColor(row.level)}
                variant={getLevelVariant(row.level)}
              />
            ))}
          </Stack>
        </Stack>
      </Paper>
    );
  }

  renderEventSummary() {
    const {
      selectedGroupKey,
    } = this.state;

    const visibleLogs = this.getVisibleLogs();
    const groups = buildEventSummary(visibleLogs);

    if (!groups.length) {
      return (
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            borderRadius: 2,
            bgcolor: '#fff',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            No platform logs found for the selected filters.
          </Typography>
        </Paper>
      );
    }

    return (
      <Paper
        variant="outlined"
        sx={{
          borderRadius: 2,
          overflow: 'hidden',
          bgcolor: '#fff',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
        }}
      >
        <Box sx={{ px: 1.5, py: 0.75 }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            justifyContent="space-between"
            spacing={1}
          >
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Summary by event
              </Typography>

              <Tooltip title="Events are ordered by attention level and most recent activity. Repeated identical errors are shown as occurrences.">
                <IconButton
                  size="small"
                  aria-label="How event groups are ordered and counted"
                  sx={{ p: 0.25, color: 'text.secondary' }}
                >
                  <InfoOutlinedIcon sx={{ fontSize: 17 }} />
                </IconButton>
              </Tooltip>
            </Stack>

            <Chip
              size="small"
              variant="outlined"
              label={`${groups.length} groups`}
            />
          </Stack>
        </Box>

        <Divider />

        <Box
          sx={{
            display: { xs: 'block', sm: 'none' },
            flex: 1,
            minHeight: 0,
            overflow: 'auto',
            p: 1,
          }}
        >
          <Stack spacing={1}>
            {groups.map((group) => {
              const isSelected = selectedGroupKey === group.key;
              const needsReview = group.criticalCount > 0 || group.errorCount > 0;

              return (
                <Paper
                  key={group.key}
                  variant="outlined"
                  onClick={() => this.selectGroup(group)}
                  sx={{
                    p: 1,
                    borderRadius: 2,
                    cursor: 'pointer',
                    bgcolor: isSelected ? 'rgba(25, 118, 210, 0.08)' : '#fff',
                    borderColor: needsReview
                      ? 'rgba(211, 47, 47, 0.45)'
                      : 'rgba(15, 23, 42, 0.12)',
                  }}
                >
                  <Stack spacing={0.75}>
                    <Stack
                      direction="row"
                      alignItems="flex-start"
                      justifyContent="space-between"
                      spacing={1}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <ClampedText
                          title={group.error_code || group.event_type}
                          sx={{ fontWeight: 800 }}
                        >
                          {group.error_code || group.event_type}
                        </ClampedText>

                        {group.error_code_description ? (
                          <ClampedText
                            title={group.error_code_description}
                            lines={2}
                            variant="caption"
                            color="text.secondary"
                          >
                            {group.error_code_description}
                          </ClampedText>
                        ) : null}

                        <ClampedText
                          title={`${group.source} / ${group.event_type}`}
                          variant="caption"
                          color="text.secondary"
                        >
                          {group.source} / {group.event_type}
                        </ClampedText>
                      </Box>

                      <Button
                        size="small"
                        variant={isSelected ? 'contained' : 'outlined'}
                        startIcon={<VisibilityIcon fontSize="small" />}
                        onClick={(e) => {
                          e.stopPropagation();
                          this.selectGroup(group);
                        }}
                        sx={{ flexShrink: 0 }}
                      >
                        View
                      </Button>
                    </Stack>

                    <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ rowGap: 0.75 }}>
                      <Chip size="small" variant="outlined" label={`${group.total} occurrences`} />
                      <Chip size="small" variant="outlined" label={`${group.rowCount} rows`} />

                      <Chip
                        size="small"
                        label={`${group.errorCount + group.criticalCount} errors`}
                        color={needsReview ? 'error' : 'default'}
                        variant={needsReview ? 'filled' : 'outlined'}
                      />

                      {group.warningCount > 0 ? (
                        <Chip
                          size="small"
                          color="warning"
                          variant="outlined"
                          label={`${group.warningCount} warnings`}
                        />
                      ) : null}
                    </Stack>

                    <Typography variant="caption" color="text.secondary">
                      Last event: {formatDateTime(group.lastEvent)}
                    </Typography>
                  </Stack>
                </Paper>
              );
            })}
          </Stack>
        </Box>

        <TableContainer
          sx={{
            display: { xs: 'none', sm: 'block' },
            flex: 1,
            minHeight: 0,
            overflow: 'auto',
          }}
        >
          <Table size="small" stickyHeader sx={{ tableLayout: 'fixed', minWidth: 1100 }}>
            <TableHead sx={{ '& .MuiTableCell-head': { fontWeight: 700 } }}>
              <TableRow>
                <TableCell sx={{ width: 330 }}>Event</TableCell>
                <TableCell align="right" sx={{ width: 105 }}>Occurrences</TableCell>
                <TableCell align="right" sx={{ width: 85 }}>Errors</TableCell>
                <TableCell sx={{ width: 110 }}>Source</TableCell>
                <TableCell sx={{ width: 160 }}>Last event</TableCell>
                <TableCell sx={{ width: 300 }}>Last message</TableCell>
                <TableCell align="right" sx={{ width: 100 }}>Details</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {groups.map((group) => {
                const isSelected = selectedGroupKey === group.key;
                const errors = group.errorCount + group.criticalCount;
                const needsReview = errors > 0;

                return (
                  <TableRow
                    key={group.key}
                    hover
                    selected={isSelected}
                    onClick={() => this.selectGroup(group)}
                    sx={{
                      cursor: 'pointer',
                      height: SUMMARY_ROW_HEIGHT,
                      maxHeight: SUMMARY_ROW_HEIGHT,
                      '&.Mui-selected': {
                        bgcolor: 'rgba(25, 118, 210, 0.08)',
                      },
                      '&.Mui-selected:hover': {
                        bgcolor: 'rgba(25, 118, 210, 0.12)',
                      },
                    }}
                  >
                    <TableCell sx={compactSummaryCellSx}>
                      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <ClampedText
                            title={group.error_code || group.event_type}
                            sx={{ fontWeight: 700 }}
                          >
                            {group.error_code || group.event_type}
                          </ClampedText>
                        </Box>

                        {needsReview ? (
                          <Chip
                            size="small"
                            color="error"
                            label="Needs review"
                            sx={{ height: 20, flexShrink: 0 }}
                          />
                        ) : null}
                      </Stack>

                      {group.error_code_description ? (
                        <ClampedText
                          title={group.error_code_description}
                          variant="caption"
                          color="text.secondary"
                        >
                          {group.error_code_description}
                        </ClampedText>
                      ) : null}

                      <ClampedText
                        title={group.event_type}
                        variant="caption"
                        color="text.secondary"
                      >
                        {group.event_type}
                      </ClampedText>
                    </TableCell>

                    <TableCell align="right" sx={compactSummaryCellSx}>
                      <Chip
                        size="small"
                        label={group.total}
                        variant="outlined"
                      />
                    </TableCell>

                    <TableCell align="right" sx={compactSummaryCellSx}>
                      <Chip
                        size="small"
                        label={errors}
                        color={needsReview ? 'error' : 'default'}
                        variant={needsReview ? 'filled' : 'outlined'}
                      />
                    </TableCell>

                    <TableCell sx={compactSummaryCellSx}>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={group.source}
                      />
                    </TableCell>

                    <TableCell sx={compactSummaryCellSx}>
                      <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                        {formatDateTime(group.lastEvent)}
                      </Typography>
                    </TableCell>

                    <TableCell sx={compactSummaryCellSx}>
                      <ClampedText
                        title={group.sampleMessage || '-'}
                        lines={2}
                        color="text.secondary"
                      >
                        {group.sampleMessage || '-'}
                      </ClampedText>
                    </TableCell>

                    <TableCell align="right" sx={compactSummaryCellSx}>
                      <Button
                        size="small"
                        variant={isSelected ? 'contained' : 'outlined'}
                        startIcon={<VisibilityIcon fontSize="small" />}
                        onClick={(e) => {
                          e.stopPropagation();
                          this.selectGroup(group);
                        }}
                      >
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    );
  }

  renderLogsTable() {
    const {
      logs,
      expanded,
      selectedGroupKey,
      selectedGroupName,
    } = this.state;

    const visibleLogs = selectedGroupKey
      ? logs.filter((row) => getEventGroupKey(row) === selectedGroupKey)
      : [];
    const visibleOccurrences = visibleLogs.reduce(
      (total, row) => total + getOccurrenceCount(row),
      0
    );

    if (!selectedGroupKey) {
      return (
        <Paper
          variant="outlined"
          sx={{
            p: 3,
            borderRadius: 2,
            bgcolor: '#fff',
            height: '100%',
            minHeight: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
          }}
        >
          <Box sx={{ maxWidth: 380 }}>
            <BugReportIcon sx={{ fontSize: 42, opacity: 0.35, mb: 1 }} />

            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Select an event group
            </Typography>

            <Typography variant="body2" color="text.secondary">
              Start from the event summary. Detailed platform log rows will appear here only for the selected group.
            </Typography>
          </Box>
        </Paper>
      );
    }

    if (!visibleLogs.length) {
      return (
        <Paper
          variant="outlined"
          sx={{
            borderRadius: 2,
            overflow: 'hidden',
            bgcolor: '#fff',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            p: 2,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            No logs found for the selected event group.
          </Typography>
        </Paper>
      );
    }

    return (
      <Paper
        variant="outlined"
        sx={{
          borderRadius: 2,
          overflow: 'hidden',
          bgcolor: '#fff',
          height: '100%',
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box sx={{ px: 1.5, py: 0.75, flexShrink: 0 }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            justifyContent="space-between"
            spacing={1}
          >
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
              <ClampedText
                title={`Logs for ${selectedGroupName}`}
                lines={2}
                variant="subtitle2"
                sx={{ fontWeight: 700 }}
              >
                Logs for {selectedGroupName}
              </ClampedText>

              <Tooltip title="Expand a row to inspect payload, request and business context.">
                <IconButton
                  size="small"
                  aria-label="How to inspect log details"
                  sx={{ p: 0.25, color: 'text.secondary', flexShrink: 0 }}
                >
                  <InfoOutlinedIcon sx={{ fontSize: 17 }} />
                </IconButton>
              </Tooltip>
            </Stack>

            <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
              <Chip
                size="small"
                variant="outlined"
                label={`${visibleOccurrences} occurrences`}
              />
              <Chip
                size="small"
                variant="outlined"
                label={`${visibleLogs.length} rows`}
              />

              <Button
                size="small"
                variant="outlined"
                onClick={this.closeDetailsDrawer}
              >
                Close
              </Button>
            </Stack>
          </Stack>
        </Box>

        <Divider />

        <TableContainer
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: 'auto',
          }}
        >
          <Table size="small" stickyHeader sx={{ tableLayout: 'fixed', minWidth: 1320 }}>
            <TableHead sx={{ '& .MuiTableCell-head': { fontWeight: 700 } }}>
              <TableRow>
                <TableCell sx={{ width: 42 }} />
                <TableCell sx={{ width: 145 }}>First seen</TableCell>
                <TableCell sx={{ width: 145 }}>Last seen</TableCell>
                <TableCell align="right" sx={{ width: 72 }}>Count</TableCell>
                <TableCell sx={{ width: 90 }}>Level</TableCell>
                <TableCell sx={{ width: 220 }}>Error code</TableCell>
                <TableCell sx={{ width: 230 }}>Context</TableCell>
                <TableCell sx={{ width: 300 }}>Message</TableCell>
                <TableCell align="right" sx={{ width: 76 }}>Payload</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {visibleLogs.map((row, index) => {
                const rowId = row.id || `${row.createdOn}-${index}`;
                const isOpen = !!expanded[rowId];
                const hasDetail = !!row.payload || !!row.message || !!row.request_path;
                const clientPlatformLabel = getClientPlatformLabel(row);
                const contextLabel = `${row.source || '-'} / ${row.event_type || '-'}`;
                const businessContext = [
                  row.order_code ? `Order: ${row.order_code}` : '',
                  getLocationName(row) ? `Location: ${getLocationName(row)}` : '',
                ].filter(Boolean).join(' ') || '-';

                return (
                  <React.Fragment key={rowId}>
                    <TableRow hover sx={{ height: LOG_ROW_HEIGHT, maxHeight: LOG_ROW_HEIGHT }}>
                      <TableCell sx={compactLogCellSx}>
                        <IconButton
                          size="small"
                          aria-label={isOpen ? 'Hide log details' : 'View log details'}
                          onClick={() => this.toggleExpanded(rowId)}
                          disabled={!hasDetail}
                        >
                          {isOpen ? (
                            <KeyboardArrowDownIcon fontSize="small" />
                          ) : (
                            <KeyboardArrowRightIcon fontSize="small" />
                          )}
                        </IconButton>
                      </TableCell>

                      <TableCell sx={{ ...compactLogCellSx, whiteSpace: 'nowrap' }}>
                        <Typography variant="body2">
                          {formatDateTime(row.createdOn)}
                        </Typography>
                      </TableCell>

                      <TableCell sx={{ ...compactLogCellSx, whiteSpace: 'nowrap' }}>
                        <Typography variant="body2">
                          {formatDateTime(getLastSeenOn(row))}
                        </Typography>
                      </TableCell>

                      <TableCell align="right" sx={compactLogCellSx}>
                        <Chip
                          size="small"
                          label={getOccurrenceCount(row)}
                          color={getOccurrenceCount(row) > 1 ? 'warning' : 'default'}
                          variant={getOccurrenceCount(row) > 1 ? 'filled' : 'outlined'}
                        />
                      </TableCell>

                      <TableCell sx={compactLogCellSx}>
                        <Chip
                          size="small"
                          label={row.level_label || formatLevelLabel(row.level)}
                          color={getLevelColor(row.level)}
                          variant={getLevelVariant(row.level)}
                        />
                      </TableCell>

                      <TableCell sx={compactLogCellSx}>
                        <Stack spacing={0.25}>
                          <ClampedText
                            title={row.error_code || '-'}
                            lines={2}
                            sx={{ fontWeight: 700 }}
                          >
                            {row.error_code || '-'}
                          </ClampedText>

                          {row.error_code_description ? (
                            <ClampedText
                              title={row.error_code_description}
                              variant="caption"
                              color="text.secondary"
                            >
                              {row.error_code_description}
                            </ClampedText>
                          ) : null}
                        </Stack>
                      </TableCell>

                      <TableCell sx={compactLogCellSx}>
                        <Stack spacing={0.25}>
                          <ClampedText title={contextLabel}>
                            {contextLabel}
                          </ClampedText>

                          {clientPlatformLabel ? (
                            <Chip
                              size="small"
                              label={clientPlatformLabel}
                              variant="outlined"
                              sx={{ alignSelf: 'flex-start', height: 20 }}
                            />
                          ) : null}

                          <ClampedText
                            title={businessContext}
                            variant="caption"
                            color="text.secondary"
                          >
                            {businessContext}
                          </ClampedText>
                        </Stack>
                      </TableCell>

                      <TableCell sx={compactLogCellSx}>
                        <ClampedText title={row.message || '-'} lines={3}>
                          {row.message || '-'}
                        </ClampedText>
                      </TableCell>

                      <TableCell align="right" sx={compactLogCellSx}>
                        {hasDetail ? (
                          <Chip
                            size="small"
                            label={isOpen ? 'Hide' : 'View'}
                            variant="outlined"
                            onClick={() => this.toggleExpanded(rowId)}
                          />
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            —
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>

                    <TableRow>
                      <TableCell
                        colSpan={9}
                        sx={{
                          p: 0,
                          borderBottom: isOpen ? '1px solid rgba(224, 224, 224, 1)' : 0,
                        }}
                      >
                        <Collapse in={isOpen} timeout="auto" unmountOnExit>
                          <Box
                            sx={{
                              px: 2,
                              py: 1.5,
                              bgcolor: 'rgba(15, 23, 42, 0.025)',
                            }}
                          >
                            <Box
                              sx={{
                                display: 'grid',
                                gridTemplateColumns: {
                                  xs: '1fr',
                                  md: 'repeat(2, minmax(0, 1fr))',
                                  lg: 'repeat(4, minmax(0, 1fr))',
                                },
                                gap: 1.25,
                                '& > *': {
                                  minWidth: 0,
                                },
                                '& > * > .MuiTypography-body2': {
                                  overflowWrap: 'anywhere',
                                  wordBreak: 'break-word',
                                },
                              }}
                            >
                              <Box>
                                <Typography variant="caption" color="text.secondary">
                                  Source
                                </Typography>
                                <Typography variant="body2">
                                  {normalizeText(row.source)}
                                </Typography>
                              </Box>

                              <Box>
                                <Typography variant="caption" color="text.secondary">
                                  Event type
                                </Typography>
                                <Typography variant="body2">
                                  {normalizeText(row.event_type)}
                                </Typography>
                              </Box>

                              <Box>
                                <Typography variant="caption" color="text.secondary">
                                  Object
                                </Typography>
                                <Typography variant="body2">
                                  {normalizeText(row.object_type)} / {normalizeText(row.object_iref)}
                                </Typography>
                              </Box>

                              <Box>
                                <Typography variant="caption" color="text.secondary">
                                  Request
                                </Typography>
                                <Typography variant="body2">
                                  {normalizeText(row.request_method)} {normalizeText(row.request_path)}
                                </Typography>
                              </Box>

                              <Box>
                                <Typography variant="caption" color="text.secondary">
                                  Occurrences
                                </Typography>
                                <Typography variant="body2">
                                  {getOccurrenceCount(row)}
                                </Typography>
                              </Box>

                              <Box>
                                <Typography variant="caption" color="text.secondary">
                                  Last seen
                                </Typography>
                                <Typography variant="body2">
                                  {formatDateTime(getLastSeenOn(row))}
                                </Typography>
                              </Box>

                              <Box>
                                <Typography variant="caption" color="text.secondary">
                                  Order code
                                </Typography>
                                <Typography variant="body2">
                                  {normalizeText(row.order_code)}
                                </Typography>
                              </Box>

                              <Box>
                                <Typography variant="caption" color="text.secondary">
                                  Client
                                </Typography>
                                <Typography variant="body2">
                                  {normalizeText(row.client_iref)}
                                </Typography>
                              </Box>

                              <Box>
                                <Typography variant="caption" color="text.secondary">
                                  POS
                                </Typography>
                                <Typography variant="body2">
                                  {normalizeText(row.pos_iref)}
                                </Typography>
                              </Box>

                              <Box>
                                <Typography variant="caption" color="text.secondary">
                                  Location
                                </Typography>
                                <Typography variant="body2">
                                  {normalizeText(getLocationName(row))}
                                </Typography>
                              </Box>

                              <Box>
                                <Typography variant="caption" color="text.secondary">
                                  User
                                </Typography>
                                <Typography variant="body2">
                                  {getUserLabel(row)}
                                </Typography>
                              </Box>

                              <Box>
                                <Typography variant="caption" color="text.secondary">
                                  User type
                                </Typography>
                                <Typography variant="body2">
                                  {normalizeText(row.user_type)}
                                </Typography>
                              </Box>
                            </Box>

                            <Box sx={{ mt: 1.25 }}>
                              <Typography variant="caption" color="text.secondary">
                                Message
                              </Typography>

                              <Typography
                                variant="body2"
                                sx={{
                                  mt: 0.25,
                                  p: 1,
                                  borderRadius: 1.5,
                                  bgcolor: '#fff',
                                  border: '1px solid rgba(15, 23, 42, 0.08)',
                                  whiteSpace: 'pre-wrap',
                                  wordBreak: 'break-word',
                                }}
                              >
                                {row.message || 'No message available.'}
                              </Typography>
                            </Box>

                            <Box sx={{ mt: 1.25 }}>
                              <Typography variant="caption" color="text.secondary">
                                Payload
                              </Typography>

                              <Typography
                                component="pre"
                                variant="body2"
                                sx={{
                                  mt: 0.25,
                                  p: 1,
                                  borderRadius: 1.5,
                                  bgcolor: '#fff',
                                  border: '1px solid rgba(15, 23, 42, 0.08)',
                                  fontFamily: 'monospace',
                                  whiteSpace: 'pre-wrap',
                                  wordBreak: 'break-word',
                                  overflow: 'auto',
                                  maxHeight: 360,
                                  m: 0,
                                }}
                              >
                                {prettyPayload(row.payload)}
                              </Typography>
                            </Box>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    );
  }

  renderDetailsDrawer() {
    const {
      detailsDrawerOpen,
      selectedGroupName,
      selectedGroupKey,
      logs,
    } = this.state;

    const visibleLogs = selectedGroupKey
      ? logs.filter((row) => getEventGroupKey(row) === selectedGroupKey)
      : [];

    return (
      <Drawer
        anchor="right"
        open={detailsDrawerOpen}
        onClose={this.closeDetailsDrawer}
        PaperProps={{
          sx: {
            width: {
              xs: '100%',
              sm: 760,
              md: 980,
              lg: 1280,
              xl: 1400,
            },
            maxWidth: '100%',
            bgcolor: 'background.default',
          },
        }}
      >
        <Box
          sx={{
            height: '100dvh',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            p: { xs: 1, sm: 1.5 },
          }}
        >
          <Paper
            variant="outlined"
            sx={{
              borderRadius: 2,
              bgcolor: '#fff',
              mb: 1,
              px: 1.5,
              py: 1.25,
              flexShrink: 0,
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={1}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="subtitle1"
                  title={selectedGroupName || 'Platform logs'}
                  sx={{ fontWeight: 800 }}
                  noWrap
                >
                  {selectedGroupName || 'Platform logs'}
                </Typography>

                <Typography variant="caption" color="text.secondary">
                  {visibleLogs.reduce((total, row) => total + getOccurrenceCount(row), 0)} occurrences in {visibleLogs.length} rows
                </Typography>
              </Box>

              <Stack direction="row" spacing={0.75} alignItems="center">
                <Tooltip title="Export selected logs">
                  <span>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<DownloadIcon fontSize="small" />}
                      onClick={this.exportSelectedGroupLogs}
                      disabled={!visibleLogs.length}
                      sx={{
                        minHeight: 34,
                        display: { xs: 'none', sm: 'inline-flex' },
                      }}
                    >
                      Export
                    </Button>
                  </span>
                </Tooltip>

                <Tooltip title="Export selected logs">
                  <span>
                    <IconButton
                      size="small"
                      onClick={this.exportSelectedGroupLogs}
                      disabled={!visibleLogs.length}
                      sx={{
                        display: { xs: 'inline-flex', sm: 'none' },
                        border: '1px solid rgba(15, 23, 42, 0.12)',
                        borderRadius: 1.5,
                      }}
                    >
                      <DownloadIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>

                <Tooltip title="Close details">
                  <IconButton onClick={this.closeDetailsDrawer}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Stack>
          </Paper>

          <Box
            sx={{
              flex: 1,
              minHeight: 0,
            }}
          >
            {this.renderLogsTable()}
          </Box>
        </Box>
      </Drawer>
    );
  }

  render() {
    const { loading, error } = this.state;

    return (
      <Box
        sx={{
          p: { xs: 1, sm: 1.5, md: 2 },
          bgcolor: 'background.default',
          height: {
            xs: 'calc(100dvh - 56px)',
            md: 'calc(100dvh - 64px)',
          },
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Stack
          spacing={{ xs: 0.75, sm: 1.25 }}
          sx={{
            height: '100%',
            minHeight: 0,
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              Platform logs
            </Typography>

            <Typography variant="body2" color="text.secondary">
              Backend-generated events and failed endpoint attempts. Start from grouped issues, then inspect details only when needed.
            </Typography>
          </Box>

          {this.renderFilters()}

          {error ? (
            <Alert severity="error">
              {error}
            </Alert>
          ) : null}

          {loading ? (
            <Alert severity="info" icon={<CircularProgress size={18} />}>
              Loading platform logs...
            </Alert>
          ) : null}

          {this.renderSummary()}

          {this.renderLevelChips()}

          <Box
            sx={{
              flex: 1,
              minHeight: 0,
            }}
          >
            {this.renderEventSummary()}
          </Box>

          {this.renderDetailsDrawer()}
        </Stack>
      </Box>
    );
  }
}

export default PlatformLogs;
